/**
 * Admin V5 — PTE Production Restore.
 * /admin/pte-restore
 *
 * Uses existing admin cookie auth. Never touches DSAT, Selective Entry,
 * users, attempts, XP, payments or bookings. Idempotent + scoped to
 * exam_type=pte_academic.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, CheckCircle2, AlertCircle, PlayCircle, RotateCcw } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const ax = axios.create({ baseURL: BACKEND, withCredentials: true });

const CHUNK = 40;
const MAX_CHUNKS = 5;   // 4 covers 134; 5 gives safety margin

export default function AdminPTERestorePage() {
  const [status, setStatus] = useState(null);
  const [log, setLog] = useState([]);
  const [running, setRunning] = useState(false);
  const [verify, setVerify] = useState(null);

  const push = (t) => setLog((l) => [...l, `[${new Date().toLocaleTimeString()}] ${t}`]);

  const loadStatus = useCallback(async () => {
    try {
      const r = await ax.get('/api/pte/admin/restore/status');
      setStatus(r.data);
    } catch (e) {
      push(`status failed: ${e?.response?.status || e.message}`);
    }
  }, []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  const run = async (kind) => {
    setRunning(true); setVerify(null);
    try {
      if (kind === 'dry') {
        push('Running dry run…');
        const q = await ax.post('/api/pte/admin/restore/questions?dry_run=true');
        push(`Dry run — questions: would insert ${q.data.inserted}, update ${q.data.updated}`);
        const v = await ax.post('/api/pte/admin/restore/vocab?dry_run=true');
        push(`Dry run — vocabulary: would insert ${v.data.inserted}, update ${v.data.updated}`);
      } else if (kind === 'full') {
        push('Step 1/3 — restoring questions…');
        const q = await ax.post('/api/pte/admin/restore/questions');
        push(`  inserted ${q.data.inserted}, updated ${q.data.updated}`);
        push('Step 2/3 — restoring vocabulary…');
        const v = await ax.post('/api/pte/admin/restore/vocab');
        push(`  inserted ${v.data.inserted}, updated ${v.data.updated}`);
        push('Step 3/3 — generating media (chunked)…');
        for (let i = 0; i < MAX_CHUNKS; i++) {
          const offset = i * CHUNK;
          const m = await ax.post(`/api/pte/admin/restore/media?offset=${offset}&limit=${CHUNK}`);
          push(`  chunk offset=${offset}: audio +${m.data.audio_generated_this_call}, image +${m.data.image_generated_this_call}`);
          if (m.data.audio_generated_this_call === 0 && i > 0) { push('  media generation complete.'); break; }
        }
        push('Restore complete. Running verification…');
        const ver = await ax.get('/api/pte/admin/restore/verify');
        setVerify(ver.data);
        push(`Overall parity match: ${ver.data.parity?.overall_match}`);
      } else if (kind === 'verify') {
        const ver = await ax.get('/api/pte/admin/restore/verify');
        setVerify(ver.data);
        push(`Overall parity match: ${ver.data.parity?.overall_match}`);
      }
      await loadStatus();
    } catch (e) {
      push(`FAILED: ${e?.response?.status || e.message} — ${e?.response?.data?.detail || ''}`);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="admin-pte-restore">
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 space-y-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-amber-400 font-bold">Admin V5 · PTE</div>
          <h1 className="text-3xl md:text-4xl font-bold mt-1">PTE Production Restore</h1>
          <p className="text-slate-400 text-sm mt-1 max-w-2xl">
            Idempotent restore of the Phase 1 PTE dataset (253 questions, 156 vocabulary entries, 134 gTTS audio clips, 12 SVG Describe Image visuals) into this environment.
            Never touches Digital SAT, Selective Entry, users, attempts, XP, payments or bookings.
          </p>
        </div>

        {status && (
          <div className="rounded-2xl bg-slate-900 border border-slate-800 p-5" data-testid="restore-status">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-3">Current state</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <StatCell label="Questions" value={status.questions.total} expected={status.questions.expected} sub={`${status.questions.published} published`} />
              <StatCell label="Vocabulary" value={status.vocabulary.total} expected={status.vocabulary.expected} />
              <StatCell label="Audio files" value={status.audio.gridfs_files} expected={status.audio.expected} sub={`${status.audio.attached_to_questions} attached`} />
              <StatCell label="Image files" value={status.images.gridfs_files} expected={status.images.expected} sub={`${status.images.attached_to_questions} attached`} />
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <button onClick={() => run('dry')} disabled={running} data-testid="restore-dry"
            className="px-4 py-2 rounded-full bg-slate-800 hover:bg-slate-700 text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50">
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
            Dry run
          </button>
          <button onClick={() => run('full')} disabled={running} data-testid="restore-full"
            className="px-4 py-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-bold inline-flex items-center gap-2 disabled:opacity-50">
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
            Restore PTE content (idempotent)
          </button>
          <button onClick={() => run('verify')} disabled={running} data-testid="restore-verify"
            className="px-4 py-2 rounded-full bg-indigo-500 hover:bg-indigo-400 text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50">
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            Verify Live PTE
          </button>
          <button onClick={loadStatus} disabled={running} data-testid="restore-refresh"
            className="px-4 py-2 rounded-full bg-slate-800 hover:bg-slate-700 text-white text-sm font-semibold inline-flex items-center gap-2">
            <RotateCcw className="h-4 w-4" /> Refresh
          </button>
        </div>

        {verify && (
          <div className={`rounded-2xl border p-5 ${verify.parity?.overall_match ? 'bg-emerald-950 border-emerald-800' : 'bg-amber-950 border-amber-800'}`}
            data-testid="restore-verify-result">
            <div className="text-xs uppercase tracking-widest font-bold mb-2 flex items-center gap-1">
              {verify.parity?.overall_match
                ? <><CheckCircle2 className="h-3.5 w-3.5" /> Parity match</>
                : <><AlertCircle className="h-3.5 w-3.5" /> Parity mismatch</>}
            </div>
            <pre className="text-xs text-slate-300 overflow-x-auto whitespace-pre-wrap" data-testid="restore-verify-json">
              {JSON.stringify(verify.parity, null, 2)}
            </pre>
            <div className="mt-3 text-xs uppercase tracking-widest text-slate-500">Mock readiness</div>
            <pre className="text-xs text-slate-300 overflow-x-auto whitespace-pre-wrap">
              {JSON.stringify(verify.mock_readiness?.items, null, 2)}
            </pre>
          </div>
        )}

        <div className="rounded-2xl bg-black border border-slate-800 p-4 h-64 overflow-auto font-mono text-xs text-slate-300" data-testid="restore-log">
          {log.length === 0 ? <div className="text-slate-600">Log output will appear here…</div> :
            log.map((l, i) => <div key={i}>{l}</div>)}
        </div>
      </div>
    </div>
  );
}

const StatCell = ({ label, value, expected, sub }) => {
  const ok = value >= expected;
  return (
    <div className={`rounded-xl border p-3 ${ok ? 'bg-emerald-950/50 border-emerald-800' : 'bg-amber-950/50 border-amber-800'}`}>
      <div className="text-[10px] uppercase tracking-widest text-slate-400">{label}</div>
      <div className="text-2xl font-bold">
        {value} <span className="text-xs text-slate-500 font-normal">/ {expected}</span>
      </div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
};
